import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { StoryService } from '../services/StoryService';
import { StoryResponse } from '../types/story';
import { logger } from '../utils/logger';
import '../styles/Pages.css';

const Home: React.FC = () => {
  const { user, getAccessToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [recentStories, setRecentStories] = useState<StoryResponse[]>([]);
  const [favoriteStories, setFavoriteStories] = useState<StoryResponse[]>([]);
  const [loadingRecent, setLoadingRecent] = useState(true);
  const [loadingFavorites, setLoadingFavorites] = useState(true);
  const [errorRecent, setErrorRecent] = useState<string | null>(null);
  const [errorFavorites, setErrorFavorites] = useState<string | null>(null);
  
  // Track if the component has been mounted
  const didMountRef = useRef(false);
  // Track if we're currently fetching to prevent duplicate fetches
  const isFetchingRef = useRef(false);
  
  // Function to fetch stories
  const fetchStories = async () => {
    // Prevent duplicate fetches
    if (isFetchingRef.current) return;
    
    // Set fetching flag
    isFetchingRef.current = true;
    
    // Reset error states
    setErrorRecent(null);
    setErrorFavorites(null);
    
    if (!user) {
      setLoadingRecent(false);
      setLoadingFavorites(false);
      isFetchingRef.current = false;
      return;
    }
    
    // Get token once for both fetches
    const token = await getAccessToken();
    if (!token) {
      setLoadingRecent(false);
      setLoadingFavorites(false);
      isFetchingRef.current = false;
      return;
    }
    
    logger.log('🔄 Fetching stories for Home page');
    
    // Fetch stories in parallel for better performance
    try {
      const [userStories, favorites] = await Promise.all([
        // Fetch user stories
        (async () => {
          try {
            setLoadingRecent(true);
            const stories = await StoryService.getUserStories(token);
            logger.log(`✅ Fetched ${stories.length} user stories`);
            return stories;
          } catch (err) {
            setErrorRecent(err instanceof Error ? err.message : 'Failed to load recent stories');
            logger.error('Error fetching recent stories:', err);
            return [];
          } finally {
            setLoadingRecent(false);
          }
        })(),
        
        // Fetch favorite stories
        (async () => {
          try {
            setLoadingFavorites(true);
            const favorites = await StoryService.getFavoriteStories(token);
            logger.log(`✅ Fetched ${favorites.length} favorite stories`);
            return favorites;
          } catch (err) {
            setErrorFavorites(err instanceof Error ? err.message : 'Failed to load favorite stories');
            logger.error('Error fetching favorite stories:', err);
            return [];
          } finally {
            setLoadingFavorites(false);
          }
        })()
      ]);
      
      // Process favorite stories first
      const markedFavorites = favorites.map(story => ({
        ...story,
        isFavorite: true,
        is_favorite: true
      }));
      
      // Process recent stories with favorite status
      const favoriteIds = new Set(markedFavorites.map(story => story.id));
      const sortedStories = userStories
        .sort((a, b) => {
          const dateA = new Date(a.createdAt || 0).getTime();
          const dateB = new Date(b.createdAt || 0).getTime();
          return dateB - dateA;
        })
        .slice(0, 3)
        .map(story => ({
          ...story,
          isFavorite: story.id ? favoriteIds.has(story.id) : false,
          is_favorite: story.id ? favoriteIds.has(story.id) : false
        }));
      
      // Set both states at once to reduce renders
      setFavoriteStories(markedFavorites);
      setRecentStories(sortedStories);
    } catch (error) {
      logger.error('Error fetching stories:', error);
    } finally {
      // Clear fetching flag
      isFetchingRef.current = false;
    }
  };

  // This effect handles both initial mount and navigation back to the home page
  useEffect(() => {
    // On initial mount, fetch stories and mark as mounted
    if (!didMountRef.current) {
      didMountRef.current = true;
      fetchStories();
      return;
    }
    
    // On subsequent renders with location changes, check if we're at home route
    const isHomeRoute = location.pathname === '/app' || location.pathname === '/';
    if (isHomeRoute) {
      fetchStories();
    }
  }, [location.key]); // location.key changes with each navigation

  // Helper function to format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    
    // Check for SQL "zero date" (0001-01-01)
    if (dateString.startsWith('0001-01-01') || dateString === '0001-01-01T00:00:00Z') {
      return 'Recently'; // or any fallback text you prefer
    }
    
    try {
      // Use a simple date format string
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Recently';
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      // If date parsing fails, return a fallback
      return 'Recently';
    }
  };

  // Handle viewing a story
  const handleViewStory = (story: StoryResponse) => {
    // Store the story in session storage to access it on the view page
    sessionStorage.setItem('viewStory', JSON.stringify(story));
    navigate(`/story/${story.id}`);
  };

  // Toggle favorite status of a story
  const handleToggleFavorite = async (story: StoryResponse, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent triggering the card click
    
    try {
      const token = await getAccessToken();
      if (!token) return;
      
      // Get current favorite status from either property
      const currentFavoriteStatus = story.isFavorite || story.is_favorite || false;
      const newFavoriteStatus = !currentFavoriteStatus;
      
      await StoryService.toggleFavorite(story.id!, newFavoriteStatus, token);
      
      // Update lists with the new favorite status
      setRecentStories(prev => prev.map(s => 
        s.id === story.id ? {...s, isFavorite: newFavoriteStatus, is_favorite: newFavoriteStatus} : s
      ));
      
      // If toggling off favorite, remove from favorites list
      if (!newFavoriteStatus) {
        setFavoriteStories(prev => prev.filter(s => s.id !== story.id));
      } else {
        // If it's becoming a favorite and not in the list, add it
        const isInFavorites = favoriteStories.some(s => s.id === story.id);
        if (!isInFavorites) {
          setFavoriteStories(prev => [{...story, isFavorite: true, is_favorite: true}, ...prev]);
        }
      }
    } catch (err) {
      logger.error('Error toggling favorite status:', err);
    }
  };

  // Render a story list item
  const renderStoryItem = (story: StoryResponse, showFavorite: boolean = true) => {
    // Handle both snake_case and camelCase property names
    const isFavorite = story.isFavorite || story.is_favorite || false;
    
    return (
      <div 
        key={story.id} 
        className="story-list-item"
        onClick={() => handleViewStory(story)}
        style={{ cursor: 'pointer' }}
      >
        <div className="story-list-content">
          <h3 className="story-list-title clickable">
            {story.title}
          </h3>
          <span className="story-list-date">
            Created on {formatDate(story.createdAt)}
          </span>
        </div>
        {showFavorite && (
          <button 
            className={`favorite-button ${isFavorite ? 'favorited' : ''}`}
            onClick={(e) => handleToggleFavorite(story, e)}
            aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
            title={isFavorite ? "Remove from favorites" : "Add to favorites"}
          >
            {isFavorite ? "★" : "☆"}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="page-container dashboard-page">
      <div className="dashboard-welcome">
        <h1>Welcome back, {user?.email?.split('@')[0] || 'Storyteller'}!</h1>
        <p>What magical story will you create today?</p>
      </div>
      
      <div className="dashboard-grid">
        <div className="dashboard-card quick-actions">
          <h2 className="dashboard-section-title">Quick Actions</h2>
          <div className="quick-action-buttons">
            <Link to="/create-story" className="quick-action-button primary">
              <span className="quick-action-icon">✨</span>
              <span className="quick-action-text">Create New Story</span>
            </Link>
            <Link to="/my-stories" className="quick-action-button secondary">
              <span className="quick-action-icon">📚</span>
              <span className="quick-action-text">My Stories</span>
            </Link>
            <Link to="/workshop" className="quick-action-button secondary">
              <span className="quick-action-icon">🧙‍♂️</span>
              <span className="quick-action-text">Story Workshop</span>
            </Link>
          </div>
        </div>
        
        <div className="dashboard-card recent-stories">
          <h2 className="dashboard-section-title">Recent Stories</h2>
          {loadingRecent ? (
            <div className="loading-indicator">
              <div className="mini-spinner"></div>
              <p>Loading your stories...</p>
            </div>
          ) : errorRecent ? (
            <div className="error-message">
              <p>{errorRecent}</p>
              <button 
                className="retry-button"
                onClick={() => window.location.reload()}
              >
                Retry
              </button>
            </div>
          ) : recentStories.length > 0 ? (
            <div className="story-list">
              {recentStories.map(story => renderStoryItem(story))}
              {recentStories.length > 0 && (
                <Link to="/my-stories" className="see-all-link">
                  See all stories <span className="arrow-icon">→</span>
                </Link>
              )}
            </div>
          ) : (
            <div className="empty-list-message">
              <p>You haven't created any stories yet.</p>
              <Link to="/create-story" className="action-button primary">Create Your First Story</Link>
            </div>
          )}
        </div>
        
        <div className="dashboard-card favorite-stories">
          <h2 className="dashboard-section-title">Favorite Stories</h2>
          {loadingFavorites ? (
            <div className="loading-indicator">
              <div className="mini-spinner"></div>
              <p>Loading your favorites...</p>
            </div>
          ) : errorFavorites ? (
            <div className="error-message">
              <p>{errorFavorites}</p>
              <button 
                className="retry-button"
                onClick={() => window.location.reload()}
              >
                Retry
              </button>
            </div>
          ) : favoriteStories.length > 0 ? (
            <div className="story-list">
              {favoriteStories.map(story => renderStoryItem(story, false))}
              {favoriteStories.length > 3 && (
                <Link to="/my-stories" className="see-all-link">
                  See all favorites <span className="arrow-icon">→</span>
                </Link>
              )}
            </div>
          ) : (
            <div className="empty-list-message">
              <p>You don't have any favorite stories yet.</p>
              <p className="empty-subtitle">Mark stories as favorites by clicking the ☆ icon on any story!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Home; 